import type { ChipProps } from '@mui/material';

import { formatAdminNumber } from '../../../utils/numberFormat';
import type { AiLogOrigin } from '../../../types/aiLog';

export type VarianceTone = Extract<ChipProps['color'], 'default' | 'success' | 'warning' | 'error'>;

const signedPercent = (value: number): string => {
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(1)}%`;
};

export const formatVarianceLabel = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return '—';
  return signedPercent(value);
};

export const varianceColor = (value: number | null, outOfTolerance: boolean): VarianceTone => {
  if (value === null || !Number.isFinite(value)) return 'default';
  if (outOfTolerance && value > 0) return 'error';
  if (outOfTolerance && value < 0) return 'warning';
  return 'success';
};

export const varianceTooltip = (value: number | null, outOfTolerance: boolean): string => {
  if (value === null || !Number.isFinite(value)) return 'No Cloudflare cost yet';
  if (outOfTolerance && value > 0) return 'System estimate is higher than Cloudflare';
  if (outOfTolerance && value < 0) {
    return 'System estimate is lower than Cloudflare (we may undercharge)';
  }
  return 'Within tolerance';
};

export const formatLatency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (value >= 1000) return `${(value / 1000).toFixed(1)} s`;
  return `${Math.round(value)} ms`;
};

export const formatUsd = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  }).format(value);
};

export const formatCredits = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return formatAdminNumber(value, { maximumFractionDigits: 0 });
};

export const originLabel = (origin: AiLogOrigin | string | null): string => {
  switch (origin) {
    case 'aife':
      return 'aife UI';
    case 'api_key':
      return 'API key';
    case 'code_agent':
      return 'Coding agent';
    case 'system':
      return 'System';
    default:
      return origin || 'Unknown';
  }
};

export const latencySourceLabel = (source: string | null): string => {
  if (source === 'entrypoint') return 'measured from request received';
  if (source === 'gateway') return 'measured inside gateway only';
  return source || 'Latency source unavailable';
};

export const formatScalar = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value || '—';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return '—';
};
